import {
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  Box,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Logout as LogoutIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { shallowEqual } from 'react-redux';
import * as auth from '@/modules/auth/redux/AuthRedux';
import { getEmployeeByUser } from '@/modules/employees/redux/EmployeeCRUD';
import CompanySelectionModal from '@/modules/auth/components/CompanySelectionModal';
import ProfileUpdateModal from './ProfileUpdateModal';
import type { UserModel } from '@/modules/auth/models/UserModel';
import { getUserByToken, getUserCompaniesAndRoles } from '@/modules/auth/redux/AuthCRUD';
import {
  hydrateAuthCompanies,
  logoutAndRedirectToLogin,
  redirectAfterCompanySwitch,
} from '@/modules/auth/utils/authUtils';
import { saveSelectedCompany, type CompanyData } from '@/modules/auth/helpers/CompanyStorage';
import type { RootState } from '@/setup/redux/RootReducer';
import {
  normalizeProfileForAuth,
  resolveProfileAvatarUrl,
} from '@/modules/auth/utils/avatarUtils';

interface ProfileMenuProps {
  size?: 'small' | 'medium' | 'large';
  showAvatar?: boolean;
}

type AuthProfileUser = {
  id?: number | string;
  email?: string;
  firstname?: string;
  lastname?: string;
  first_name?: string;
  last_name?: string;
  fullname?: string;
  full_name?: string;
  nickname?: string;
  alias?: string;
  display_name?: string;
  companyName?: string;
  pic?: string;
  avatar?: string;
};

const toDisplayText = (value: unknown): string => {
  const text = String(value ?? '').trim();
  return text;
};

const resolveDisplayName = (user: AuthProfileUser | undefined, employeeNickname: string): string => {
  const nickname = toDisplayText(employeeNickname)
    || toDisplayText(user?.nickname)
    || toDisplayText(user?.alias)
    || toDisplayText(user?.display_name);

  if (nickname) {
    return nickname;
  }

  const fullName = toDisplayText(user?.fullname)
    || toDisplayText(user?.full_name)
    || [toDisplayText(user?.firstname || user?.first_name), toDisplayText(user?.lastname || user?.last_name)]
      .filter(Boolean)
      .join(' ');

  return fullName || toDisplayText(user?.email) || '—';
};

const resolveAvatarInitials = (displayName: string, email: string): string => {
  const source = displayName && displayName !== '—' ? displayName : email;
  const parts = source.trim().split(/\s+/).filter(Boolean);

  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }

  if (parts.length === 1 && parts[0].length > 0) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return 'U';
};

export default function ProfileMenu({ size = 'small', showAvatar = true }: ProfileMenuProps) {
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [employeeNickname, setEmployeeNickname] = useState('');
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [modalCompanies, setModalCompanies] = useState<CompanyData[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [avatarSrc, setAvatarSrc] = useState('');
  const [avatarLoadError, setAvatarLoadError] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const avatarButtonRef = useRef<HTMLButtonElement>(null);
  const open = Boolean(anchorEl);

  const user = useSelector((state: RootState) => state.auth.user, shallowEqual) as AuthProfileUser | undefined;
  const selectedCompany = useSelector((state: RootState) => state.auth.selectedCompany, shallowEqual);
  const authCompanies = useSelector(
    (state: RootState) => (state.auth.companies ?? []) as CompanyData[],
    shallowEqual
  );
  const hasGlobalRole = useSelector((state: RootState) => Boolean(state.auth.hasGlobalRole));

  const canChooseCompany = useMemo(() => {
    if (hasGlobalRole) {
      return true;
    }
    const uniqueCompanyIds = new Set(
      authCompanies.map((company: CompanyData) => company.company_id).filter(Boolean)
    );
    return uniqueCompanyIds.size >= 2;
  }, [authCompanies, hasGlobalRole]);

  useEffect(() => {
    const userId = user?.id;
    if (!userId) {
      setEmployeeNickname('');
      return;
    }

    let isActive = true;

    getEmployeeByUser(String(userId))
      .then((response) => {
        if (!isActive) {
          return;
        }
        if (response.status !== 200) {
          setEmployeeNickname('');
          return;
        }
        setEmployeeNickname(toDisplayText(response.data?.nickname));
      })
      .catch(() => {
        if (isActive) {
          setEmployeeNickname('');
        }
      });

    return () => {
      isActive = false;
    };
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id || authCompanies.length > 0) {
      return;
    }
    void hydrateAuthCompanies(dispatch).catch((error) => {
      console.error('Error hydrating companies for profile menu:', error);
    });
  }, [user?.id, authCompanies.length, dispatch]);

  useEffect(() => {
    const userId = user?.id;
    if (!userId) {
      setAvatarSrc('');
      return;
    }

    let isActive = true;

    const syncProfileAvatar = async () => {
      try {
        const response = await getUserByToken();
        if (!isActive) {
          return;
        }
        const normalized = normalizeProfileForAuth(
          response.data as unknown as Record<string, unknown>
        );
        dispatch(auth.actions.setUser(normalized as unknown as UserModel));
        setAvatarSrc(resolveProfileAvatarUrl(normalized));
        setAvatarLoadError(false);
      } catch {
        if (isActive) {
          setAvatarSrc('');
        }
      }
    };

    void syncProfileAvatar();

    return () => {
      isActive = false;
    };
  }, [user?.id, dispatch]);

  useEffect(() => {
    if (!avatarSrc) {
      setAvatarSrc(resolveProfileAvatarUrl(user));
    }
  }, [user?.pic, user?.avatar, user, avatarSrc]);

  useEffect(() => {
    setAvatarLoadError(false);
  }, [avatarSrc]);

  const displayAvatarSrc = avatarSrc || resolveProfileAvatarUrl(user);

  const companyName = useMemo(() => {
    return (
      toDisplayText(selectedCompany?.company_name)
      || toDisplayText(user?.companyName)
      || '—'
    );
  }, [selectedCompany?.company_name, user?.companyName]);

  const displayName = useMemo(
    () => resolveDisplayName(user, employeeNickname),
    [employeeNickname, user]
  );

  const email = useMemo(() => toDisplayText(user?.email) || '—', [user?.email]);

  const avatarInitials = useMemo(
    () => resolveAvatarInitials(displayName, email),
    [displayName, email]
  );

  const profileTooltip = useMemo(
    () => (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, py: 0.25 }}>
        <Typography variant='caption' sx={{ fontWeight: 600, lineHeight: 1.4 }}>
          {companyName}
        </Typography>
        <Typography variant='caption' sx={{ lineHeight: 1.4 }}>
          {displayName}
        </Typography>
        <Typography variant='caption' sx={{ lineHeight: 1.4, opacity: 0.92 }}>
          {email}
        </Typography>
      </Box>
    ),
    [companyName, displayName, email]
  );

  const handleClick = () => {
    setTooltipOpen(false);
    setAnchorEl(avatarButtonRef.current);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setTooltipOpen(false);
  };

  const handleLogout = () => {
    handleClose();
    void logoutAndRedirectToLogin(dispatch);
  };

  const handleProfile = () => {
    handleClose();
    setShowProfileModal(true);
  };

  const handleProfileModalClose = () => {
    setShowProfileModal(false);
    void (async () => {
      try {
        const response = await getUserByToken();
        const normalized = normalizeProfileForAuth(
          response.data as unknown as Record<string, unknown>
        );
        dispatch(auth.actions.setUser(normalized as unknown as UserModel));
        setAvatarSrc(resolveProfileAvatarUrl(normalized));
        setAvatarLoadError(false);
      } catch {
        setAvatarSrc(resolveProfileAvatarUrl(user));
      }
    })();
  };

  const loadCompaniesForModal = async () => {
    try {
      setLoadingCompanies(true);
      const response = await getUserCompaniesAndRoles();
      const companiesData = (response.data?.companies ?? []) as CompanyData[];
      setModalCompanies(companiesData);
      dispatch(auth.actions.setCompanies(companiesData));
    } catch (error) {
      console.error('Error loading companies for selection:', error);
      setModalCompanies([]);
    } finally {
      setLoadingCompanies(false);
    }
  };

  const handleOpenCompanySelection = () => {
    handleClose();
    setShowCompanyModal(true);
    void loadCompaniesForModal();
  };

  const handleCompanyModalClose = () => {
    setShowCompanyModal(false);
  };

  const handleCompanySelection = (company: CompanyData) => {
    try {
      saveSelectedCompany(company, true);

      dispatch(auth.actions.setCompanies(modalCompanies.length > 0 ? modalCompanies : authCompanies));

      dispatch(
        auth.actions.setSelectedCompany({
          company_id: company.company_id,
          company_name: company.company_name,
          company_code: company.company_code,
          company_type: company.company_type,
          roles: company.roles.map((role: CompanyData['roles'][number]) => ({
            role_id: role.role_id,
            role_name: role.role_name,
            role_display_name: role.role_display_name,
            role_level: role.role_level || 'company',
            permissions: role.permissions || {},
            is_primary: role.is_primary,
            created_at: role.created_at,
          })),
          selected_at: new Date().toISOString(),
        })
      );

      setShowCompanyModal(false);
      redirectAfterCompanySwitch();
    } catch (error) {
      console.error('Error selecting company:', error);
    }
  };

  const getAvatarSize = () => {
    switch (size) {
      case 'small':
        return { width: 24, height: 24, fontSize: '12px' };
      case 'medium':
        return { width: 40, height: 40, fontSize: '16px' };
      case 'large':
        return { width: 48, height: 48, fontSize: '18px' };
      default:
        return { width: 32, height: 32, fontSize: '14px' };
    }
  };

  const getIconButtonSize = () => {
    switch (size) {
      case 'small':
        return {
          width: 24,
          height: 24,
          padding: 0,
          minWidth: 24,
          minHeight: 24,
          '& .MuiIconButton-root': {
            padding: 0,
          },
        };
      case 'medium':
        return {
          width: 40,
          height: 40,
          padding: 0,
          minWidth: 40,
          minHeight: 40,
          '& .MuiIconButton-root': {
            padding: 0,
          },
        };
      case 'large':
        return {
          width: 48,
          height: 48,
          padding: 0,
          minWidth: 48,
          minHeight: 48,
          '& .MuiIconButton-root': {
            padding: 0,
          },
        };
      default:
        return {
          width: 32,
          height: 32,
          padding: 0,
          minWidth: 32,
          minHeight: 32,
          '& .MuiIconButton-root': {
            padding: 0,
          },
        };
    }
  };

  return (
    <>
      {showAvatar && (
        <Tooltip
          title={profileTooltip}
          arrow
          placement='bottom-end'
          enterDelay={300}
          open={open ? false : tooltipOpen}
          onOpen={() => {
            if (!open) {
              setTooltipOpen(true);
            }
          }}
          onClose={() => setTooltipOpen(false)}
          disableHoverListener={open}
          disableFocusListener={open}
          disableTouchListener={open}
          slotProps={{
            tooltip: {
              sx: {
                maxWidth: 320,
                px: 1.25,
                py: 0.75,
              },
            },
          }}
        >
          <IconButton
            ref={avatarButtonRef}
            onClick={handleClick}
            aria-controls={open ? 'account-menu' : undefined}
            aria-haspopup='true'
            aria-expanded={open ? 'true' : undefined}
            sx={{
              ...getIconButtonSize(),
              p: 0,
              flexShrink: 0,
              alignSelf: 'center',
              borderRadius: '50%',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <Avatar
              src={!avatarLoadError && displayAvatarSrc ? displayAvatarSrc : undefined}
              imgProps={{
                referrerPolicy: 'no-referrer',
                onError: () => setAvatarLoadError(true),
              }}
              sx={{
                ...getAvatarSize(),
                bgcolor: '#1e3a8a',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: 0,
                flexShrink: 0,
              }}
            >
              {avatarInitials}
            </Avatar>
          </IconButton>
        </Tooltip>
      )}

      <Menu
        anchorEl={anchorEl}
        id='account-menu'
        open={open}
        onClose={handleClose}
        disableScrollLock
        disableAutoFocus
        autoFocus={false}
        marginThreshold={8}
        PaperProps={{
          elevation: 3,
          sx: {
            overflow: 'visible',
            mt: 1.5,
            minWidth: 180,
            borderRadius: 2,
            border: '1px solid #e2e8f0',
            '& .MuiMenuItem-root': {
              py: 1.5,
              px: 2,
              fontSize: '14px',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: '#f8fafc',
              },
            },
            '& .MuiListItemIcon-root': {
              minWidth: 36,
              color: '#64748b',
            },
            '& .MuiDivider-root': {
              my: 0.5,
              borderColor: '#e2e8f0',
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: -6,
              right: 16,
              width: 12,
              height: 12,
              bgcolor: 'white',
              border: '1px solid #e2e8f0',
              borderBottom: 'none',
              borderRight: 'none',
              transform: 'rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleProfile}>
          <ListItemIcon>
            <PersonIcon fontSize='small' />
          </ListItemIcon>
          Profile
        </MenuItem>
       
        {canChooseCompany && (
          <MenuItem onClick={handleOpenCompanySelection} disabled={loadingCompanies}>
            <ListItemIcon>
              <BusinessIcon fontSize='small' />
            </ListItemIcon>
            {loadingCompanies ? 'Đang tải...' : 'Chọn công ty'}
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={handleLogout} sx={{ color: '#ef4444' }}>
          <ListItemIcon>
            <LogoutIcon fontSize='small' sx={{ color: '#ef4444' }} />
          </ListItemIcon>
          Sign out
        </MenuItem>
      </Menu>

      <ProfileUpdateModal
        open={showProfileModal}
        onClose={handleProfileModalClose}
        user={user as UserModel | undefined}
      />

      <CompanySelectionModal
        open={showCompanyModal}
        onClose={handleCompanyModalClose}
        onSelectCompany={handleCompanySelection}
        companies={modalCompanies.length > 0 ? modalCompanies : authCompanies}
        loading={loadingCompanies}
        disabledCompanyId={selectedCompany?.company_id}
      />
    </>
  );
}
